import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AgentDefinition } from 'app/modules/agent-library/models/agent-definition.model';
import { AgentSession } from 'app/modules/agent-library/models/agent-run.model';
import { AgentActivationMeta, AgentReversibilityMeta, AgentTaxonomyMeta } from 'app/modules/agent-library/models/agent-taxonomy.model';
import { AGENT_ACTIVATION_META, AGENT_AUTONOMY_META, AGENT_REVERSIBILITY_META } from 'app/modules/agent-library/services/agent-taxonomy.constants';

/** How much of the agent the card shows. */
export type AgentCardVariant = 'full' | 'compact';

/**
 * One agent, summarised. The library landing renders the `full` variant; anywhere that needs to say
 * *which* agent is being talked to renders `compact` — the DEO assistant's agent selector is the
 * next consumer.
 *
 * Presentational on purpose: it takes a definition and, for the KPI summary, a count. It reads no
 * service, so it renders the same inside the library, inside the assistant, or in a spec.
 *
 * @example
 * ```html
 * <gains-agent-card [agent]="agent" [enabledKpiCount]="enabledFor(agent)" (opened$)="open($event)"></gains-agent-card>
 * <gains-agent-card [agent]="agent" variant="compact"></gains-agent-card>
 * ```
 */
@Component({
  selector: 'gains-agent-card',
  standalone: false,
  templateUrl: './gains-agent-card.component.html',
  styleUrl: './gains-agent-card.component.scss'
})
export class GainsAgentCardComponent {
  @Input() agent?: AgentDefinition;

  @Input() variant: AgentCardVariant = 'full';

  /**
   * How many of the agent's KPIs are switched on, which is configuration rather than definition —
   * so the host supplies it and the card stays free of the config service.
   */
  @Input() enabledKpiCount = 0;

  @Output() opened$ = new EventEmitter<AgentDefinition>();

  @Output() sessionOpened$ = new EventEmitter<AgentSession>();

  get isCompact(): boolean {
    return this.variant === 'compact';
  }

  /** The agent's initials, e.g. `SLA`. Kept in step with `AgentsService.resolveAgentMark`. */
  get mark(): string {
    return (this.agent?.name || '')
      .split(/\s+/)
      .filter(word => !!word)
      .slice(0, 3)
      .map(word => word[0].toUpperCase())
      .join('');
  }

  get autonomyMeta(): AgentTaxonomyMeta | undefined {
    return this.agent ? AGENT_AUTONOMY_META[this.agent.autonomy] : undefined;
  }

  get reversibilityMeta(): AgentReversibilityMeta | undefined {
    return this.agent ? AGENT_REVERSIBILITY_META[this.agent.reversibility] : undefined;
  }

  get activationMetas(): AgentActivationMeta[] {
    return (this.agent?.activations ?? []).map(activation => AGENT_ACTIVATION_META[activation]);
  }

  /** `Available` reads green, a roadmap agent reads as a neutral state rather than a problem. */
  get availabilityTone(): string {
    return this.agent?.isAvailable ? 'green' : 'grey';
  }

  /** An unavailable agent can still be inspected, so the call to action says so. */
  get callToActionLabel(): string {
    return this.agent?.isAvailable ? 'Open' : 'Details';
  }

  get hasKpis(): boolean {
    return (this.agent?.kpis?.length ?? 0) > 0;
  }

  get kpiSummary(): string {
    return `${this.enabledKpiCount} of ${this.agent?.kpis.length} KPIs monitored`;
  }

  get hasSessions(): boolean {
    return (this.agent?.activeSessions?.length ?? 0) > 0;
  }

  get sessionCountLabel(): string {
    const count = this.agent?.activeSessions.length ?? 0;
    return `${count} ${count === 1 ? 'session' : 'sessions'}`;
  }

  open(): void {
    if (this.agent) {
      this.opened$.emit(this.agent);
    }
  }

  /** A session is a shortcut past the card, so it must not also open the agent behind it. */
  openSession(session: AgentSession, event: Event): void {
    event.stopPropagation();
    this.sessionOpened$.emit(session);
  }

  protected trackByLabel(index: number, meta: AgentTaxonomyMeta): string {
    return meta.label;
  }

  protected trackById(index: number, session: AgentSession): string {
    return session.id;
  }
}
